import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import type { ReportFilters as FilterType } from '../types/report';

interface ReportFiltersProps {
  filters: FilterType;
  onChange: (filters: FilterType) => void;
}

/**
 * Date range filter for reports
 * Includes This Month and Last Month preset buttons
 */
function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function ReportFilters({ filters, onChange }: ReportFiltersProps) {
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...filters, startDate: e.target.value || undefined });
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...filters, endDate: e.target.value || undefined });
  };

  const handleThisMonth = () => {
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    onChange({
      startDate: formatLocalDate(firstOfMonth),
      endDate: formatLocalDate(now),
    });
  };

  const handleLastMonth = () => {
    const now = new Date();
    const firstOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    onChange({
      startDate: formatLocalDate(firstOfLastMonth),
      endDate: formatLocalDate(lastOfLastMonth),
    });
  };

  const handleClear = () => {
    onChange({});
  };

  return (
    <Card>
      <CardContent className="pt-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[150px]">
            <Label htmlFor="startDate">Start Date</Label>
            <Input
              id="startDate"
              type="date"
              value={filters.startDate ?? ''}
              onChange={handleStartDateChange}
            />
          </div>

          <div className="flex-1 min-w-[150px]">
            <Label htmlFor="endDate">End Date</Label>
            <Input
              id="endDate"
              type="date"
              value={filters.endDate ?? ''}
              onChange={handleEndDateChange}
            />
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={handleThisMonth}>
              This Month
            </Button>
            <Button variant="outline" onClick={handleLastMonth}>
              Last Month
            </Button>
            <Button variant="ghost" onClick={handleClear}>
              Clear
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
